import { type Application, applications } from '@labeling/globals';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useApi } from '@/providers/api';
import { useToastContext } from '@/providers/toast';
import { useUserStore } from '@/stores/user-store';
import { isAdminOrMaintainer } from '@/utils/permissions';
import { assertResponseOk, toErrorMessage } from '@/utils/response';
import type {
  ConfirmDialogState,
  FormAccessGroup,
  GroupSelection,
  KnownOperator,
  OperatorAccessEntry,
  Selection,
} from './types';
import {
  groupsForOperator as computeGroupsForOperator,
  GROUP_NAME_REGEX,
  projectApplicationAccess,
} from './utils';

export const useOperatorAccess = () => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { user } = useUserStore();
  const { showToast } = useToastContext();

  const manageableApplications = useMemo<Application[]>(() => {
    if (isAdminOrMaintainer(user)) return [...applications];
    if (user.type === 'user') return user.applications;
    return [];
  }, [user]);

  const [selection, setSelection] = useState<Selection>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(null);
  const confirmTriggerRef = useRef<HTMLElement>(null);

  const {
    data: assignments,
    isLoading: assignmentsLoading,
    mutate: reloadAssignments,
  } = useSWR(['operator-access', 'all'], async () => {
    const res = await client.v1['operator-access'].$get({ query: {} });
    return (await res.json()) as { items: OperatorAccessEntry[] };
  });

  const { data: allKnownOperators, mutate: reloadOperators } = useSWR(
    ['known-operators', 'all'],
    async () => {
      const res = await client.v1['operator-access']['known-operators'].$get({
        query: {},
      });
      return (await res.json()) as { items: KnownOperator[] };
    }
  );

  const {
    data: formAccessGroups,
    isLoading: formGroupsLoading,
    mutate: reloadFormGroups,
  } = useSWR(
    manageableApplications.length > 0
      ? ['form-access-groups', manageableApplications.join(',')]
      : null,
    async () => {
      const result: Partial<Record<Application, FormAccessGroup[]>> = {};
      await Promise.all(
        manageableApplications.map(async (app) => {
          const res = await client.v1['form-access-groups'].$get({ query: { application: app } });
          if (!res.ok) throw new Error(`Failed to load form access groups (${res.status})`);
          const body = (await res.json()) as { items: FormAccessGroup[] };
          result[app] = body.items ?? [];
        })
      );
      return result;
    }
  );

  const applicationAccess = useMemo(
    () =>
      projectApplicationAccess(
        manageableApplications,
        assignments,
        allKnownOperators,
        formAccessGroups
      ),
    [manageableApplications, assignments, allKnownOperators, formAccessGroups]
  );

  const selectedApplication = selection
    ? applicationAccess.find((access) => access.application === selection.application)
    : undefined;

  const selectedMembers = useMemo<OperatorAccessEntry[]>(() => {
    if (selection?.kind !== 'group') return [];
    return selectedApplication?.groups.get(selection.groupName) ?? [];
  }, [selection, selectedApplication]);

  const assignableOperators = useMemo(() => {
    if (selection?.kind !== 'group') return [];
    const memberIds = new Set(selectedMembers.map((member) => member.operatorId));
    return (allKnownOperators?.items ?? []).filter(
      (operator) =>
        operator.operatorApplications.includes(selection.application) && !memberIds.has(operator.id)
    );
  }, [allKnownOperators, selection, selectedMembers]);

  const selectGroup = (application: Application, groupName: string) => {
    setSelection({ kind: 'group', application, groupName });
  };

  const selectUnrestricted = (application: Application) => {
    setSelection({ kind: 'unrestricted', application });
  };

  const validateGroupName = (name: string) => {
    const groupName = name.trim().toLowerCase();
    if (GROUP_NAME_REGEX.test(groupName)) return groupName;
    showToast(t('operatorAccessPage.response.invalidGroupName'), 'error');
    return null;
  };

  const commitNewGroup = async (app: Application, name: string): Promise<boolean> => {
    const groupName = validateGroupName(name);
    if (!groupName) return false;
    setIsBusy(true);
    try {
      const res = await client.v1['form-access-groups'].$post({
        json: { application: app, groupName },
      });
      await assertResponseOk(res, t('operatorAccessPage.response.saveFailed'));
      await reloadFormGroups();
      selectGroup(app, groupName);
      return true;
    } catch (e) {
      showToast(toErrorMessage(e, t('operatorAccessPage.response.saveFailed')), 'error');
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  const setGroupsForOperator = async (
    operatorId: number,
    application: Application,
    groupIds: number[]
  ) => {
    const res = await client.v1['operator-access'].$post({
      json: { operatorId, application, groupIds },
    });
    await assertResponseOk(res, t('operatorAccessPage.response.saveFailed'));
  };

  const groupsForOperator = (operatorId: number, application: Application) =>
    computeGroupsForOperator(assignments, operatorId, application);

  const findGroupId = (application: Application, groupName: string) =>
    formAccessGroups?.[application]?.find((candidate) => candidate.groupName === groupName)?.id;

  const addOperatorToSelectedGroup = async (operatorId: number) => {
    if (selection?.kind !== 'group') return;
    const { application, groupName } = selection;
    const groupId = findGroupId(application, groupName);
    if (groupId === undefined) throw new Error(t('operatorAccessPage.response.saveFailed'));
    const groupIds = new Set([...groupsForOperator(operatorId, application), groupId]);
    await setGroupsForOperator(operatorId, application, [...groupIds]);
  };

  const runMutation = async (
    action: () => Promise<void>,
    successKey: string,
    failureKey: string,
    onSuccess: () => Promise<unknown> = reloadAssignments,
    onFailure?: () => Promise<unknown>
  ): Promise<boolean> => {
    setIsBusy(true);
    try {
      await action();
      showToast(t(successKey), 'success');
      await onSuccess();
      return true;
    } catch (e) {
      showToast(toErrorMessage(e, t(failureKey)), 'error');
      await onFailure?.();
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  const assignKnownOperator = async (operatorId: number) => {
    if (selection?.kind !== 'group') return false;
    return runMutation(
      () => addOperatorToSelectedGroup(operatorId),
      'operatorAccessPage.response.saveSuccess',
      'operatorAccessPage.response.saveFailed'
    );
  };

  const inviteAndAssign = async (email: string) => {
    if (selection?.kind !== 'group') return false;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      showToast(t('operatorAccessPage.response.missingEmail'), 'error');
      return false;
    }
    return runMutation(
      async () => {
        const inviteRes = await client.v1['operator-access'].invite.$post({
          json: { email: trimmedEmail },
        });
        await assertResponseOk(inviteRes, t('operatorAccessPage.response.inviteFailed'));
        const { operator } = (await inviteRes.json()) as { operator: KnownOperator };
        await addOperatorToSelectedGroup(operator.id);
      },
      'operatorAccessPage.response.inviteSuccess',
      'operatorAccessPage.response.inviteFailed',
      () => Promise.all([reloadAssignments(), reloadOperators()])
    );
  };

  const requestRemoveMember = (entry: OperatorAccessEntry) => {
    confirmTriggerRef.current = document.activeElement as HTMLElement;
    setConfirmDialog({ type: 'removeMember', entry });
  };

  const requestDeleteGroup = () => {
    if (selection?.kind !== 'group') return;
    confirmTriggerRef.current = document.activeElement as HTMLElement;
    setConfirmDialog({ type: 'deleteGroup', selection });
  };

  const closeConfirmDialog = () => {
    if (!isBusy) setConfirmDialog(null);
  };

  const confirmRemoveMember = async (entry: OperatorAccessEntry): Promise<void> => {
    await runMutation(
      async () => {
        const res = await client.v1['operator-access'][':id'].$delete({
          param: { id: String(entry.id) },
        });
        await assertResponseOk(res, t('operatorAccessPage.response.deleteFailed'));
      },
      'operatorAccessPage.response.removeMemberSuccess',
      'operatorAccessPage.response.deleteFailed',
      async () => {
        setConfirmDialog(null);
        await reloadAssignments();
      }
    );
  };

  // No row (404) is fine: the group is already gone.
  const deleteGroupRow = async (group: GroupSelection) => {
    // Look the group up fresh: cached state may be stale or empty after a failed fetch, and
    // treating a persisted group as assignment-only would skip the 409 protection.
    const listRes = await client.v1['form-access-groups'].$get({
      query: { application: group.application },
    });
    await assertResponseOk(listRes, t('operatorAccessPage.response.deleteFailed'));
    const { items } = (await listRes.json()) as { items: FormAccessGroup[] };
    const groupId = items?.find((candidate) => candidate.groupName === group.groupName)?.id;
    if (groupId === undefined) return;
    const res = await client.v1['form-access-groups'][':id'].$delete({
      param: { id: String(groupId) },
    });
    if (res.status !== 404) {
      await assertResponseOk(res, t('operatorAccessPage.response.deleteFailed'));
    }
  };

  // Deleting the group row also removes its operator assignments (ON DELETE CASCADE), so the
  // members need no separate requests. A 409 (still linked to forms) leaves everything untouched.
  const confirmDeleteGroup = async (group: GroupSelection): Promise<void> => {
    setIsBusy(true);
    try {
      await deleteGroupRow(group);
      showToast(t('operatorAccessPage.response.deleteGroupSuccess'), 'success');
      setConfirmDialog(null);
      setSelection(null);
      await Promise.all([reloadAssignments(), reloadFormGroups()]);
    } catch (e) {
      showToast(toErrorMessage(e, t('operatorAccessPage.response.deleteFailed')), 'error');
    } finally {
      setIsBusy(false);
    }
  };

  const renameGroup = async (target: string): Promise<boolean> => {
    if (selection?.kind !== 'group') return false;
    const groupName = validateGroupName(target);
    if (!groupName) return false;
    if (groupName === selection.groupName) return true;
    return runMutation(
      async () => {
        const groupId = findGroupId(selection.application, selection.groupName);
        if (groupId === undefined) throw new Error(t('operatorAccessPage.response.saveFailed'));
        // Form links and operator assignments reference the group by id, so only the name changes.
        const res = await client.v1['form-access-groups'][':id'].$patch({
          param: { id: String(groupId) },
          json: { groupName },
        });
        await assertResponseOk(res, t('operatorAccessPage.response.saveFailed'));
      },
      'operatorAccessPage.response.saveSuccess',
      'operatorAccessPage.response.saveFailed',
      async () => {
        setSelection({ ...selection, groupName });
        await Promise.all([reloadAssignments(), reloadFormGroups()]);
      },
      () => Promise.all([reloadAssignments(), reloadFormGroups()])
    );
  };

  const restrictOperator = async (operatorId: number, group: string): Promise<boolean> => {
    if (selection?.kind !== 'unrestricted') return false;
    const groupName = validateGroupName(group);
    if (!groupName) return false;
    return runMutation(
      async () => {
        let groupId = findGroupId(selection.application, groupName);
        if (groupId === undefined) {
          // A name typed freely may not exist yet: create the group first.
          const created = await client.v1['form-access-groups'].$post({
            json: { application: selection.application, groupName },
          });
          await assertResponseOk(created, t('operatorAccessPage.response.saveFailed'));
          groupId = ((await created.json()) as { id: number }).id;
        }
        await setGroupsForOperator(operatorId, selection.application, [groupId]);
      },
      'operatorAccessPage.response.saveSuccess',
      'operatorAccessPage.response.saveFailed',
      () => Promise.all([reloadAssignments(), reloadFormGroups()])
    );
  };

  return {
    // Groups name the assignments, so the page waits for both before showing who is in which group.
    assignmentsLoading: assignmentsLoading || formGroupsLoading,
    assignmentsLoaded: Boolean(assignments) && !formGroupsLoading,
    applicationAccess,
    selectedApplication,
    selection,
    selectGroup,
    selectUnrestricted,
    selectedMembers,
    assignableOperators,
    isBusy,
    confirmDialog,
    confirmTriggerRef,
    closeConfirmDialog,
    commitNewGroup,
    assignKnownOperator,
    inviteAndAssign,
    requestRemoveMember,
    requestDeleteGroup,
    confirmRemoveMember,
    confirmDeleteGroup,
    renameGroup,
    restrictOperator,
  };
};
